/**
 * Modal spécifique pour la création de prise de besoin
 * Plan Directeur TI - Analyse Technique & Budget
 */

import { useEffect, useState, useMemo } from 'react';
import { X, Building2, Server, Clock, HardDrive, Shield, FileText, DollarSign, Code, Users, ChevronRight, ChevronLeft, Save } from 'lucide-react';
import { getIntakeTemplate } from '../domain/besoins/templates';
import { createBesoin, updateBesoin, getBesoinById } from '../services/besoinsService';
import type { ClientItem } from '../services/clientService';
import { getAllClients } from '../services/clientService';

interface PriseDeBesoinPlanDirecteurTechniqueModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
  templateId?: string;
  templateVersion?: string;
  besoinId?: number;
  onSuccess?: (besoinId: number) => void;
}

// Mapping des icônes par nom
const iconMap: Record<string, any> = {
  Building2,
  Server,
  Clock,
  HardDrive,
  Shield,
  FileText,
  DollarSign,
  Code,
  Users,
};

export default function PriseDeBesoinPlanDirecteurTechniqueModal({
  isOpen,
  onClose,
  clientId = 2,
  templateId = 'plan-directeur-technique-v1',
  templateVersion = '1.0.0',
  besoinId,
  onSuccess,
}: PriseDeBesoinPlanDirecteurTechniqueModalProps) {
  const [step, setStep] = useState<number>(1);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [nomBesoin, setNomBesoin] = useState<string>('');
  const [descriptionBesoin, setDescriptionBesoin] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<number | ''>(clientId || '');
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [showPriseClient] = useState<boolean>(true);
  const [makeBy, setMakeBy] = useState<string>('');
  const [archived, setArchived] = useState<boolean>(false);

  // Charger le template
  const template = useMemo(() => {
    try {
      return getIntakeTemplate(templateId);
    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      return null;
    }
  }, [templateId]);

  // Nombre de phases du template
  const maxSteps = useMemo(() => {
    return template?.phases.length || 0;
  }, [template]);

  // Phase actuelle
  const currentPhase = useMemo(() => {
    if (!template || step < 1 || step > template.phases.length) return null;
    return template.phases[step - 1];
  }, [template, step]);

  // Charger les données existantes au montage
  useEffect(() => {
    if (isOpen && besoinId) {
      getBesoinById(besoinId)
        .then((besoin) => {
          if (besoin) {
            setNomBesoin(besoin.nom || '');
            setDescriptionBesoin(besoin.description || '');
            setSelectedClientId(besoin.client_id || '');
            setMakeBy(besoin.makeby || '');
            setArchived(besoin.archived || false);
            // Reconstruire responses à partir des phase_data
            const allResponses: Record<string, any> = {};
            for (let i = 1; i <= 10; i++) {
              const phaseData = besoin[`phase${i}_data` as keyof typeof besoin] as Record<string, any> | undefined;
              if (phaseData) {
                Object.assign(allResponses, phaseData);
              }
            }
            setResponses(allResponses);
          }
        })
        .catch((error) => {
          console.error('Erreur lors du chargement du besoin:', error);
        });
    } else if (isOpen && !besoinId) {
      // Réinitialiser pour une nouvelle prise de besoin
      setStep(1);
      setResponses({});
      setNomBesoin('');
      setDescriptionBesoin('');
      setSelectedClientId(clientId || '');
      setMakeBy('');
      setArchived(false);
    }
  }, [isOpen, besoinId, clientId]);

  // Charger les clients
  useEffect(() => {
    if (isOpen) {
      setIsLoadingClients(true);
      getAllClients()
        .then((data) => {
          setClients(data);
        })
        .catch((error) => {
          console.error('Erreur lors du chargement des clients:', error);
        })
        .finally(() => {
          setIsLoadingClients(false);
        });
    }
  }, [isOpen]);

  // Rendu d'une question
  const renderQuestion = (q: any) => {
    const value = responses[q.id] || '';
    const isRequired = q.required || false;

    switch (q.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => setResponses({ ...responses, [q.id]: e.target.value })}
            placeholder={q.placeholder || ''}
            required={isRequired}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
          />
        );
      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => setResponses({ ...responses, [q.id]: e.target.value })}
            placeholder={q.placeholder || ''}
            required={isRequired}
            rows={q.rows || 6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-bz-blue focus:border-bz-blue resize-y font-mono text-xs"
          />
        );
      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => setResponses({ ...responses, [q.id]: e.target.value })}
            required={isRequired}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
          >
            <option value="">Sélectionner...</option>
            {q.options?.map((opt: string, idx: number) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {q.options?.map((opt: string, idx: number) => (
              <label key={idx} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setResponses({ ...responses, [q.id]: [...selectedValues, opt] });
                    } else {
                      setResponses({ ...responses, [q.id]: selectedValues.filter((v) => v !== opt) });
                    }
                  }}
                  className="w-4 h-4 text-bz-blue focus:ring-bz-blue border-gray-300 rounded"
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  // Sauvegarder
  const handleSave = async () => {
    if (!selectedClientId || !template) return;

    setIsSaving(true);
    try {
      // Organiser les réponses par phase
      const phaseData: Record<string, Record<string, any>> = {};
      template.phases.forEach((phase, index) => {
        const phaseResponses: Record<string, any> = {};
        phase.questions.forEach((q) => {
          if (responses[q.id] !== undefined) {
            phaseResponses[q.id] = responses[q.id];
          }
        });
        if (Object.keys(phaseResponses).length > 0) {
          phaseData[`phase${index + 1}_data`] = phaseResponses;
        }
      });

      const besoinData: any = {
        nom: nomBesoin || `Plan Directeur TI - Analyse Technique & Budget - ${new Date().toLocaleDateString('fr-CA')}`,
        description: descriptionBesoin || template.description,
        client_id: Number(selectedClientId),
        template_id: templateId,
        template_version: templateVersion,
        ...phaseData,
        makeby: makeBy,
        archived: archived,
      };

      if (besoinId) {
        await updateBesoin(besoinId, besoinData);
      } else {
        const newBesoin = await createBesoin(besoinData);
        if (newBesoin?.id && onSuccess) {
          onSuccess(newBesoin.id);
        }
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;
  if (!template) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-xl p-6 max-w-md">
          <p className="text-red-600">Template introuvable: {templateId}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[currentPhase?.icon || 'FileText'] || FileText;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[95vh] my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Server className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{template.name}</h2>
              <p className="text-xs text-white/80">{template.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Informations générales (si nouvelle prise de besoin) */}
        {!besoinId && showPriseClient && (
          <div className="px-6 py-4 bg-gray-50 border-b">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Client <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  disabled={isLoadingClients}
                >
                  <option value="">Sélectionner un client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nom}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nom du besoin</label>
                <input
                  type="text"
                  value={nomBesoin}
                  onChange={(e) => setNomBesoin(e.target.value)}
                  placeholder="Ex: Analyse technique infrastructure ABC Inc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                />
              </div>
            </div>
          </div>
        )}

        {/* Contenu principal */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {currentPhase && (
            <div className="space-y-6">
              {/* En-tête de phase */}
              <div className="flex items-center gap-3 pb-4 border-b">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
                  <IconComponent className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{currentPhase.title}</h3>
                  <p className="text-sm text-gray-600">{currentPhase.description}</p>
                </div>
                <div className="text-sm text-gray-500">
                  Étape {step} / {maxSteps}
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-5">
                {currentPhase.questions.map((q: any) => (
                  <div key={q.id} className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      {q.label}
                      {q.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {q.helpText && (
                      <p className="text-xs text-gray-500 italic mb-1">{q.helpText}</p>
                    )}
                    {renderQuestion(q)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer avec navigation */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between sticky bottom-0">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Précédent
          </button>

          <div className="flex items-center gap-2">
            {step < maxSteps ? (
              <button
                onClick={() => setStep(Math.min(maxSteps, step + 1))}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 flex items-center gap-2"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={isSaving || !selectedClientId}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Sauvegarde...' : besoinId ? 'Mettre à jour' : 'Créer le formulaire'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

